import { useMemo, useCallback } from "react";
import * as Yup from "yup";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { Keyphrase, KeyphrasePolicy, KeyphraseServices } from "@/services/bedrock";

export const useComplianceChecker = (context?: { playerId?: string | number; coachId?: string | number }) => {
  const scSession = useSocialCoachSession();
  const queryClient = useQueryClient();

  const determinedPlayerId = useMemo(() => {
    return context?.playerId || scSession?.playerId;
  }, [context?.playerId, scSession?.playerId]);

  const determinedCoachId = useMemo(() => {
    return context?.coachId || scSession?.coachId;
  }, [context?.coachId, scSession?.coachId]);

  const { data: keywords = [], isLoading } = useQuery({
    queryKey: ["complianceKeywords", { playerId: determinedPlayerId, coachId: determinedCoachId }],
    queryFn: async () => {
      if (determinedPlayerId) {
        return KeyphraseServices.getKeyphrases({
          playerId: determinedPlayerId,
          policy: ["FLAG", "BLOCK"],
        });
      } else if (determinedCoachId) {
        return KeyphraseServices.getKeyphrases({
          coachId: determinedCoachId,
          policy: ["FLAG", "BLOCK"],
        });
      }
      return [];
    },
    enabled: Boolean(determinedPlayerId || determinedCoachId),
  });

  const invalidateKeywords = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["complianceKeywords"],
    });
  }, [queryClient]);

  const keywordsByPolicy = (result: Keyphrase[], policy: KeyphrasePolicy): Keyphrase[] => {
    return result.filter(keyword => keyword.policy === policy);
  };

  const blockedKeywords: Keyphrase[] = useMemo(() => {
    if (keywords && keywords.length > 0) {
      return keywordsByPolicy(keywords, "BLOCK");
    } else return [];
  }, [keywords]);

  const flaggedKeywords: Keyphrase[] = useMemo(() => {
    if (keywords && keywords.length > 0) {
      return keywordsByPolicy(keywords, "FLAG");
    } else return [];
  }, [keywords]);

  const blockedPhrases: string[] = useMemo(() => {
    return blockedKeywords.map(t => t.content.toLowerCase());
  }, [blockedKeywords]);

  const flaggedPhrases: string[] = useMemo(() => {
    return flaggedKeywords.map(t => t.content.toLowerCase());
  }, [flaggedKeywords]);

  const blockedPhraseCount = useMemo(() => {
    return blockedPhrases.length;
  }, [blockedPhrases]);

  const flaggedPhraseCount = useMemo(() => {
    return flaggedPhrases.length;
  }, [flaggedPhrases]);

  const findWords = (text: string | string[], words: string[]): string[] => {
    const content: string[] = [];

    if (text && text.length > 0) {
      if (Array.isArray(text)) {
        text.forEach(s => {
          if (s.length > 0) content.push(s);
        });
      } else {
        content.push(text);
      }
    }
    return words.filter(phrase => {
      if (!phrase) return false;
      const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(^|\\W)${escaped}($|\\W)`, "i");
      return content.some(stringToCheck => regex.test(stringToCheck));
    });
  };

  const findBlockedWords = useCallback(
    (text: string | string[]): string[] => {
      return findWords(text, blockedPhrases);
    },
    [blockedPhrases]
  );

  const findFlaggedWords = useCallback(
    (text: string | string[]): string[] => {
      return findWords(text, flaggedPhrases);
    },
    [flaggedPhrases]
  );

  const blockedWordsValidator = useMemo(() => {
    return Yup.string()
      .trim()
      .test({
        name: "blockedWords",
        skipAbsent: true,
        test(value, ctx) {
          const found = findBlockedWords(value || "");
          if (found.length > 0) {
            return ctx.createError({ message: "Blocked words detected: " + found.join(", ") });
          }
          return true;
        },
      });
  }, [findBlockedWords]);

  return {
    findFlaggedWords,
    findBlockedWords,
    blockedPhrases,
    blockedPhraseCount,
    flaggedPhrases,
    flaggedPhraseCount,
    blockedKeywords,
    flaggedKeywords,
    invalidateKeywords,
    blockedWordsValidator,
    isLoading,
  };
};
